import { IconName } from 'atomic/atoms/Icon';
import { IconName as NewIconName } from 'newAtomic/atoms/Icon';
import {
    AgentSessionEvent,
    SessionEventName,
    SessionOutcome,
    TalentUpdatedData,
    TALENT_PROPERTY_FIELDS,
    TALENT_MANAGEMENT_FIELDS,
    ConversationEventLocation,
    AgentSessionEventData,
    SessionCompletedData,
    ConversationLocationChangedData,
    ConsentUpdatedData,
    ConsentUpdateType,
} from 'services/Api/Conversation';
import { User } from 'services';
import dayjs from 'dayjs';
import { HOURS_IN_DAY } from 'lib/constants';
import { sortAsc } from 'lib/utils/array.utils';
import { getFormattedLocalDate } from 'lib/utils/date.utils';

export type EventColors = {
    backgroundColor: string;
    borderColor: string;
    iconColor: string;
};

export const getEventIcon = (eventName: SessionEventName, data?: AgentSessionEventData): IconName | NewIconName => {
    if (eventName === SessionEventName.SessionCompleted) {
        const completeSessionData = data as SessionCompletedData;
        switch (completeSessionData.outcome) {
            case SessionOutcome.Resolved:
                return 'statusDisabled';
            case SessionOutcome.Timeout:
                return 'outlineClock';
            case SessionOutcome.ManualTakeover:
                return 'statusDisabled';
            default:
                return 'statusDisabled';
        }
    }

    if (eventName === SessionEventName.SessionActivated || eventName === SessionEventName.SessionResumed) {
        return 'statusActive';
    }

    if (eventName === SessionEventName.SessionPutOnStandby) {
        return 'statusScheduled';
    }

    switch (eventName) {
        case SessionEventName.SummaryGenerated: {
            return 'logs';
        }
        case SessionEventName.TalentUpdated:
            return 'outlineEdit';
        case SessionEventName.ConversationLocationChanged: {
            const locationChangedData = data as ConversationLocationChangedData;
            if (locationChangedData.location === ConversationEventLocation.Archive) {
                return 'outlineArchived';
            }
            return 'outlineSpam';
        }
        case SessionEventName.ConsentUpdated: {
            const consentUpdatedData = data as ConsentUpdatedData;
            if (consentUpdatedData.consentType === ConsentUpdateType.OptOut) {
                return 'outlineCircleError2';
            }
            return 'outlineCircleSuccess';
        }
        default: {
            return 'outlineCircleInfo';
        }
    }
};

export const getEventColors = (
    eventName: SessionEventName,
    outcome?: SessionOutcome,
    location?: ConversationEventLocation,
    consentType?: ConsentUpdateType,
): EventColors => {
    if (eventName === SessionEventName.ConversationLocationChanged) {
        if (location === ConversationEventLocation.Spam) {
            return {
                backgroundColor: 'blue100',
                borderColor: 'blue200',
                iconColor: 'blueRibbon',
            };
        }
        return {
            backgroundColor: 'blue100',
            borderColor: 'blue200',
            iconColor: 'blueRibbon',
        };
    }

    if (eventName === SessionEventName.ConsentUpdated) {
        if (consentType === ConsentUpdateType.OptOut) {
            return {
                backgroundColor: 'offWhite',
                borderColor: 'blue100',
                iconColor: 'darkGray',
            };
        }
        return {
            backgroundColor: 'picton100',
            borderColor: 'picton300',
            iconColor: 'blue400',
        };
    }

    if (eventName === SessionEventName.SummaryGenerated) {
        return {
            backgroundColor: 'green100',
            borderColor: 'green300',
            iconColor: 'green400',
        };
    }

    if (eventName === SessionEventName.SessionPutOnStandby) {
        return {
            backgroundColor: '',
            borderColor: '',
            iconColor: 'yellow400',
        };
    }

    if (eventName === SessionEventName.SessionActivated || eventName === SessionEventName.SessionResumed) {
        return {
            backgroundColor: '',
            borderColor: '',
            iconColor: 'green400',
        };
    }

    if (eventName === SessionEventName.ApplicationCreatedInKomboAts) {
        return {
            backgroundColor: 'picton100',
            borderColor: 'picton300',
            iconColor: 'picton400',
        };
    }

    return {
        backgroundColor: 'picton100',
        borderColor: 'picton300',
        iconColor: 'picton400',
    };
};

export const getUserDisplayName = (userId: string, usersDict: Record<string, User>): string => {
    const user = usersDict[userId];
    if (!user) {
        return 'Unknown User';
    }
    return `${user.givenName || ''} ${user.familyName || ''}`.trim() || 'Unknown User';
};

export const getTalentPropertyDisplayName = (propertyKey: string): string => {
    const displayNames: Record<string, string> = {
        firstName: 'First name',
        lastName: 'Last name',
        phone: 'Phone',
        email: 'Email',
        whatsappUsername: 'WhatsApp username',
        location: 'Location',
        jobTitle: 'Job title',
        linkedinProfilePage: 'LinkedIn profile',
        currentEmployer: 'Current employer',
        rating: 'Rating',
        assigneeId: 'Assignee',
        statusId: 'Status',
        talentpoolId: 'Talentpool',
        tagsIdsAdded: 'Tags',
        customFields: 'Custom fields',
    };
    return displayNames[propertyKey] || propertyKey;
};

export const isTalentPropertyUpdate = (data: TalentUpdatedData): boolean => {
    return TALENT_PROPERTY_FIELDS.some(field => data[field] !== undefined);
};

export const isTalentManagementUpdate = (data: TalentUpdatedData): boolean => {
    return TALENT_MANAGEMENT_FIELDS.some(field => data[field] !== undefined);
};

export const getEventText = (event: AgentSessionEvent, usersDict: Record<string, User>): string => {
    switch (event.name) {
        case SessionEventName.SessionCompleted: {
            const data = event.data as any;
            switch (data.outcome) {
                case SessionOutcome.Resolved:
                    return 'AI agent deactivated';
                case SessionOutcome.Timeout:
                    return 'AI agent deactivated - Due to inactivity';
                case SessionOutcome.ManualTakeover: {
                    if (data.userId) {
                        const userName = getUserDisplayName(data.userId, usersDict);
                        return `AI agent deactivated by ${userName}`;
                    }
                    return 'AI agent deactivated by user';
                }
                default:
                    return 'AI agent deactivated';
            }
        }
        case SessionEventName.SessionPutOnStandby:
            return 'AI agent standby';
        case SessionEventName.SessionResumed:
            return 'AI agent resumed';
        case SessionEventName.SessionActivated:
            return 'AI agent activated';
        case SessionEventName.SummaryGenerated:
            return 'Summary';
        case SessionEventName.TalentUpdated: {
            const data = event.data as TalentUpdatedData;
            if (isTalentPropertyUpdate(data)) {
                return 'Talent details updated';
            }
            return 'Talent management updated';
        }
        case SessionEventName.ConversationLocationChanged: {
            const data = event.data as any;
            if (data.location === ConversationEventLocation.Spam) {
                return 'Conversation moved to spam';
            }
            return 'Conversation moved to archive';
        }
        case SessionEventName.ApplicationCreatedInKomboAts:
            return 'Application created';
        case SessionEventName.ConsentUpdated: {
            const data = event.data as ConsentUpdatedData;
            if (data.consentType === ConsentUpdateType.OptOut) {
                return 'Consent withdrawn';
            }
            return data.expiresAt
                ? `Consent granted until ${getFormattedLocalDate(data.expiresAt)}`
                : 'Consent granted';
        }
        default:
            return 'Event';
    }
};

const sortEventsByTime = (events: AgentSessionEvent[]): AgentSessionEvent[] => {
    return [...events].sort((a, b) => {
        const timeA = new Date(a.createdAt).getTime();
        const timeB = new Date(b.createdAt).getTime();
        return timeA === timeB ? 0 : sortAsc(timeA, timeB);
    });
};

const isWithinTimeWindow = (currentTime: dayjs.Dayjs, nextTime: dayjs.Dayjs): boolean => {
    const hoursDiff = nextTime.diff(currentTime, 'hours', true);
    return hoursDiff <= HOURS_IN_DAY;
};

const serializeValueForComparison = (value: any): string => {
    if (Array.isArray(value)) {
        const sorted = [...value].sort();
        return JSON.stringify(sorted);
    }
    return JSON.stringify(value);
};

interface AttributeIndex {
    [attribute: string]: Map<string, dayjs.Dayjs>;
}

const buildAttributeIndex = (
    sortedEvents: AgentSessionEvent[],
    currentIndex: number,
    currentTime: dayjs.Dayjs,
): AttributeIndex => {
    const index: AttributeIndex = {};
    const allAttributes = [...TALENT_PROPERTY_FIELDS, ...TALENT_MANAGEMENT_FIELDS];

    for (const attr of allAttributes) {
        index[attr] = new Map();
    }

    for (let j = currentIndex - 1; j >= 0; j--) {
        const previousEvent = sortedEvents[j];

        if (previousEvent.name !== SessionEventName.TalentUpdated) {
            continue;
        }

        const previousTime = dayjs(previousEvent.createdAt);
        if (!isWithinTimeWindow(previousTime, currentTime)) {
            break;
        }

        const previousData = previousEvent.data as TalentUpdatedData;

        for (const attr of allAttributes) {
            if (attr === 'customFields') {
                continue;
            }

            if (previousData[attr] !== undefined) {
                const serializedValue = serializeValueForComparison(previousData[attr]);
                const existingTime = index[attr].get(serializedValue);
                if (!existingTime || previousTime.isAfter(existingTime)) {
                    index[attr].set(serializedValue, previousTime);
                }
            }
        }

        const previousCustomFields = previousData.customFields as Record<string, any> | undefined;
        if (previousCustomFields) {
            if (!index.customFields) {
                index.customFields = new Map();
            }
            for (const fieldKey in previousCustomFields) {
                const serializedValue = serializeValueForComparison({ [fieldKey]: previousCustomFields[fieldKey] });
                const existingTime = index.customFields.get(serializedValue);
                if (!existingTime || previousTime.isAfter(existingTime)) {
                    index.customFields.set(serializedValue, previousTime);
                }
            }
        }
    }

    return index;
};

const findDuplicateAttributes = (
    currentData: TalentUpdatedData,
    attributeIndex: AttributeIndex,
): {
    attributesToRemove: Set<keyof TalentUpdatedData>;
    customFieldsToRemove: Set<string>;
} => {
    const attributesToRemove = new Set<keyof TalentUpdatedData>();
    const customFieldsToRemove = new Set<string>();
    const allAttributes = [...TALENT_PROPERTY_FIELDS, ...TALENT_MANAGEMENT_FIELDS];

    for (const attr of allAttributes) {
        if (attr === 'customFields') {
            continue;
        }

        if (currentData[attr] !== undefined) {
            const serializedValue = serializeValueForComparison(currentData[attr]);
            if (attributeIndex[attr]?.has(serializedValue)) {
                attributesToRemove.add(attr);
            }
        }
    }

    const currentCustomFields = currentData.customFields as Record<string, any> | undefined;
    if (currentCustomFields && attributeIndex.customFields) {
        for (const fieldKey in currentCustomFields) {
            const serializedValue = serializeValueForComparison({ [fieldKey]: currentCustomFields[fieldKey] });
            if (attributeIndex.customFields.has(serializedValue)) {
                customFieldsToRemove.add(fieldKey);
            }
        }
    }

    return { attributesToRemove, customFieldsToRemove };
};

const filterCustomFields = (
    customFields: Record<string, any> | undefined,
    customFieldsToRemove: Set<string>,
): Record<string, any> | undefined => {
    if (!customFields) {
        return undefined;
    }

    const filteredCustomFields: Record<string, any> = {};
    for (const fieldKey in customFields) {
        if (!customFieldsToRemove.has(fieldKey)) {
            filteredCustomFields[fieldKey] = customFields[fieldKey];
        }
    }

    return Object.keys(filteredCustomFields).length > 0 ? filteredCustomFields : undefined;
};

const buildFilteredEventData = (
    currentData: TalentUpdatedData,
    attributesToRemove: Set<keyof TalentUpdatedData>,
    customFieldsToRemove: Set<string>,
): Partial<TalentUpdatedData> => {
    const filteredData: Partial<TalentUpdatedData> = {};
    const allAttributes = [...TALENT_PROPERTY_FIELDS, ...TALENT_MANAGEMENT_FIELDS];

    for (const attr of allAttributes) {
        if (currentData[attr] === undefined || attributesToRemove.has(attr)) {
            continue;
        }

        if (attr === 'customFields') {
            const filteredCustomFields = filterCustomFields(
                currentData.customFields as Record<string, any> | undefined,
                customFieldsToRemove,
            );
            if (filteredCustomFields) {
                (filteredData as any)[attr] = filteredCustomFields;
            }
        } else {
            (filteredData as any)[attr] = currentData[attr];
        }
    }

    return filteredData;
};

const processTalentUpdatedEvent = (
    currentEvent: AgentSessionEvent,
    sortedEvents: AgentSessionEvent[],
    currentIndex: number,
): AgentSessionEvent | null => {
    const currentData = { ...(currentEvent.data as TalentUpdatedData) };
    const currentTime = dayjs(currentEvent.createdAt);

    const attributeIndex = buildAttributeIndex(sortedEvents, currentIndex, currentTime);

    const { attributesToRemove, customFieldsToRemove } = findDuplicateAttributes(currentData, attributeIndex);

    const filteredData = buildFilteredEventData(currentData, attributesToRemove, customFieldsToRemove);

    if (Object.keys(filteredData).length === 0) {
        return null;
    }

    return {
        ...currentEvent,
        data: filteredData,
    };
};

const shouldKeepSummaryEvent = (
    currentEvent: AgentSessionEvent,
    sortedEvents: AgentSessionEvent[],
    currentIndex: number,
): boolean => {
    const currentTime = dayjs(currentEvent.createdAt);

    for (let j = currentIndex + 1; j < sortedEvents.length; j++) {
        const nextEvent = sortedEvents[j];

        if (nextEvent.name !== SessionEventName.SummaryGenerated) {
            continue;
        }

        const nextTime = dayjs(nextEvent.createdAt);

        if (isWithinTimeWindow(currentTime, nextTime)) {
            return false;
        }

        break;
    }

    return true;
};

export const filterDuplicateAgentSessionEvents = (events: AgentSessionEvent[]): AgentSessionEvent[] => {
    if (events.length === 0) {
        return events;
    }

    const sortedEvents = sortEventsByTime(events);
    const processedEvents: AgentSessionEvent[] = [];

    for (let i = 0; i < sortedEvents.length; i++) {
        const currentEvent = sortedEvents[i];

        if (currentEvent.name === SessionEventName.TalentUpdated) {
            const processedEvent = processTalentUpdatedEvent(currentEvent, sortedEvents, i);
            if (processedEvent) {
                processedEvents.push(processedEvent);
            }
            continue;
        }

        if (currentEvent.name === SessionEventName.SummaryGenerated) {
            if (shouldKeepSummaryEvent(currentEvent, sortedEvents, i)) {
                processedEvents.push(currentEvent);
            }
            continue;
        }

        processedEvents.push(currentEvent);
    }

    return processedEvents;
};
